import { PropertyValueMap, TemplateResult, html, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlockAccordionCloseEvent, BlockAccordionOpenEvent } from '../../events';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/chevron-down';
import type { AccordionPanelSize } from './accordion-panel.model';
import styles from './accordion-panel.styles';

/**
 * @slot icon - The left icon content.Default is cat-icon-chevron-down
 * @slot before - The accordion-panel before
 * @slot header - The accordion-panel header
 * @slot default - The accordion-panel default
 * @slot after - The accordion-panel after
 * @fires bl-accordion-open - emitted when accordion panel is opened
 * @fires bl-accordion-close - emitted when accordion panel is closed.
 * @csspart base - The component's base wrapper.
 * @csspart header-wrapper - The component's header-wrapper.
 * @csspart button - The component's <button>.
 * @csspart icon-left - The component's icon-left.
 * @csspart part-icon - The component's icon slot
 * @csspart chevron-down-icon - The component's `cat-icon-chevron-down`.
 * @csspart part-before - The component's before slot.
 * @csspart header-name - The component's header-name.
 * @csspart part-header - The component's header slot.
 * @csspart icon-right - The component's icon-right.
 * @csspart part-after - The component's after slot.
 * @csspart body - The component's body.
 * @csspart body-inner - The component's inner body.
 * @csspart part-default - The component's default slot.
 */

export class BlocksAccordionPanel extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * is Active
   * 1) Panel is open when set to true. Close when set to false
   */
  @property({ type: Boolean })
  isActive = false;

  /**
   * index
   * 1) index of the accordion panel. Emit this value on panel close and open.
   */
  @property({ type: Number })
  index: number | undefined;

  /**
   * Aria labelled by attribute
   */
  @property({ type: String })
  ariaLabelledBy: string | undefined;

  /**
   * Aria level by attribute
   */
  @property({ type: String })
  arialevel = '3';

  /**
   * panel height property to enable scroll in accordion body
   */
  @property({ type: String })
  panelHeight = '0';

  /**
   * Disabled property
   */
  @property({ type: Boolean, reflect: true })
  disabled = false;

  /**
   * Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**lg** renders a large accordion header</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: AccordionPanelSize;

  /**
   * Display the toggle icon on the right side of the accordion panel
   */
  @property({ type: Boolean })
  alignToggleRight = false;

  /**
   * Role for Accordion Body
   */
  @property({ type: String })
  bodyRole: string | undefined = 'region';

  /**
   * Height State.
   */
  @state()
  height = '0';

  protected slots = new SlotController(this, null, 'icon', 'before', 'header', 'after');

  /**
   * Initialize/Update
   * 1) Set panel height on load. Active state should set panel height to content within panel
   */
  connectedCallback() {
    super.connectedCallback();
    this.id = this.id || nanoid();
    this.ariaLabelledBy = this.ariaLabelledBy || nanoid();
  }

  /**
   * Updated lifecycle
   */
  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (changedProperties.has('isActive')) {
      this.setPanelHeight();
    }
  }

  /**
   * Set panel height
   * 1) If isActive is true, set the height to panelHeight or auto
   * 2) If isActive is false, set the height to 0
   */
  private setPanelHeight = async () => {
    if (this.isActive) {
      this.height = this.panelHeight !== '0' ? this.panelHeight + 'px' : 'auto'; /* 1 */
    } else {
      this.height = '0'; /* 2 */
    }
  };

  private togglePanel = () => {
    this.toggle();
    this.setPanelHeight();
    if (this.isActive) {
      this.emitEvent('bl-accordion-open', true, this.index);

      return;
    }
    this.emitEvent('bl-accordion-close', false, this.index);
  };

  /**
   *
   * @param eventName name of the event
   * @param isExpand value
   * @param index index of the panel
   */
  private emitEvent(eventName: 'bl-accordion-open' | 'bl-accordion-close', isExpand: boolean, index: number | undefined) {
    const detailObj = { isExpand, index };
    if (eventName === 'bl-accordion-open') {
      return this.dispatch({ eventName, detailObj }) as BlockAccordionOpenEvent;
    } else {
      return this.dispatch({ eventName, detailObj }) as BlockAccordionCloseEvent;
    }
  }

  /**
   * Toggle accordion panel.
   */
  public toggle() {
    this.isActive = !this.isActive;
  }

  /**
   * Open accordion panel.
   */
  public open() {
    this.isActive = true;
    this.setPanelHeight();
  }

  /**
   * Close accordion panel.
   */
  public close() {
    this.isActive = false;
    this.setPanelHeight();
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-accordion-panel', {
      'cat-is-active': this.isActive === true,
      'cat-is-disabled': this.disabled,
      'cat-c-accordion-panel--large': this.size === 'lg',
      'cat-c-accordion-panel--toggle-right': this.alignToggleRight,
      'cat-c-accordion-panel--has-panel-height': this.panelHeight !== '0',
      'cat-c-accordion-panel--has-before': this.slots.hasSlotted('before'),
      'cat-c-accordion-panel--has-after': this.slots.hasSlotted('after')
    });

    return html`
      <div part="base" class="${componentClassNames}">
        <div part="header-wrapper" class="cat-c-accordion-panel__header" role="heading" aria-level="${ifDefined(this.arialevel)}">
          <button
            part="button"
            class="cat-c-accordion-panel__button"
            aria-expanded="${this.isActive && !this.disabled}"
            id="${ifDefined(this.ariaLabelledBy)}"
            aria-controls="${this.id}"
            @click=${this.togglePanel}
            ?disabled="${this.disabled}"
          >
            ${!this.alignToggleRight
              ? html`
                  <div part="icon--left" class="cat-c-accordion-panel__icon cat-c-accordion-panel__icon--left">
                    <slot part="part-icon" name="icon">
                      <cat-icon-chevron-down
                        part="chevron-down-icon"
                        exportparts=${this.getExportParts('chevron-down', 'icon')}
                      ></cat-icon-chevron-down>
                    </slot>
                  </div>
                `
              : html` <slot part="part-before" name="before"></slot> `}
            <div part="header-name" class="cat-c-accordion-header">
              <slot part="part-header" name="header"></slot>
            </div>
            ${this.alignToggleRight
              ? html`
                  <div part="icon--right" class="cat-c-accordion-panel__icon cat-c-accordion-panel__icon--right">
                    <slot part="part-icon" name="icon">
                      <cat-icon-chevron-down
                        part="chevron-down-icon"
                        exportparts=${this.getExportParts('chevron-down', 'icon')}
                      ></cat-icon-chevron-down>
                    </slot>
                  </div>
                `
              : html` <slot part="part-after" name="after"></slot> `}
          </button>
        </div>
        <div
          part="body"
          class="cat-c-accordion-panel__body"
          .role=${ifDefined(this.bodyRole)}
          aria-labelledby="${ifDefined(this.ariaLabelledBy)}"
          id="${this.id}"
          style=${'height: ' + this.height}
        >
          <div part="body-inner" class="cat-c-accordion-panel__body-inner">
            <slot part="part-default"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-accordion-panel') === undefined) {
  customElements.define('cat-accordion-panel', BlocksAccordionPanel);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-accordion-panel': BlocksAccordionPanel;
  }
}
